<template>
  <div class="position-relative slides">
    <hooper-container
      ref="hooperContainer"
      @afterSlide="afterSlide"
      class="carousel-slider"
      :style="{ height }"
      :settings="hooperSettings"
      @slide="updateCarousel"
    >
      <hooper-slide v-for="(item, i) in items" :key="i">
        <slot name="item" :item="item"></slot>
      </hooper-slide>
    </hooper-container>

    <div
      v-if="nav && pages > 1"
      class="
        d-flex
        justify-space-between
        px-3 px-md-4 px-lg-5
        carousel-slider-nav
      "
    >
      <a
        class="d-flex align-center justify-center pr-1 carousel-slider-nav-item"
        @click.prevent="slidePrev"
      >
        <v-icon color="grey" :size="mobile ? 20 : 30"> fa-angle-left </v-icon>
      </a>
      <a
        class="d-flex align-center justify-center pl-1 carousel-slider-nav-item"
        @click.prevent="slideNext"
      >
        <v-icon color="grey" :size="mobile ? 20 : 30"> fa-angle-right </v-icon>
      </a>
    </div>

    <div
      v-if="dots && pages > 1"
      class="d-flex justify-center mt-8 mt-md-12 carousel-pagination"
    >
      <div
        v-for="i in pages"
        @click="slideTo(i)"
        v-ripple
        :class="['carousel-pagitation-dot', i == dotActive && 'active']"
        :key="i"
      ></div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      required: true,
    },
    dots: {
      type: Boolean,
      default: false,
    },
    nav: {
      type: Boolean,
      default: false,
    },
    playSpeed: {
      type: Number,
      default: 2000,
    },
    height: {
      type: String,
      default: "auto",
    },
    itemsToShow: {
      type: Object,
      default: () => {
        return {
          xs: 2,
          sm: 4,
          md: 5,
          lg: 5,
          xl: 5,
        };
      },
    },
  },
  data() {
    return {
      dotActive: 1,
      carouselData: 0,
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.items.length / this.breakpoints);
    },
    breakpoints() {
      return this.itemsToShow[this.$vuetify.breakpoint.name];
    },
    hooperSettings() {
      const thresholds = this.$vuetify.breakpoint.thresholds;

      return {
        itemsToShow: this.itemsToShow.xs,
        itemsToSlide: 1,
        hoverPause: true,
        wheelControl: false,
        trimWhiteSpace: true,
        playSpeed: this.playSpeed,
        infiniteScroll: this.items.length > this.itemsToShow.xs,
        autoPlay: this.items.length > this.itemsToShow.xs,
        mouseDrag: this.items.length > this.itemsToShow.xs,
        touchDrag: this.items.length > this.itemsToShow.xs,
        breakpoints: {
          [thresholds.xs]: {
            itemsToShow: this.itemsToShow.sm,
            infiniteScroll: this.items.length > this.itemsToShow.sm,
            autoPlay: this.items.length > this.itemsToShow.sm,
            mouseDrag: this.items.length > this.itemsToShow.sm,
            touchDrag: this.items.length > this.itemsToShow.sm,
          },
          [thresholds.sm]: {
            itemsToShow: this.itemsToShow.md,
            infiniteScroll: this.items.length > this.itemsToShow.md,
            autoPlay: this.items.length > this.itemsToShow.md,
            mouseDrag: this.items.length > this.itemsToShow.md,
            touchDrag: this.items.length > this.itemsToShow.md,
          },
          [thresholds.md]: {
            itemsToShow: this.itemsToShow.lg,
            infiniteScroll: this.items.length > this.itemsToShow.lg,
            autoPlay: this.items.length > this.itemsToShow.lg,
            mouseDrag: this.items.length > this.itemsToShow.lg,
            touchDrag: this.items.length > this.itemsToShow.lg,
          },
          [thresholds.lg]: {
            itemsToShow: this.itemsToShow.xl,
            infiniteScroll: this.items.length > this.itemsToShow.xl,
            autoPlay: this.items.length > this.itemsToShow.xl,
            mouseDrag: this.items.length > this.itemsToShow.xl,
            touchDrag: this.items.length > this.itemsToShow.xl,
          },
        },
      };
    },
    mobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  methods: {
    slidePrev() {
      this.$refs.hooperContainer.slidePrev();
    },
    slideNext() {
      this.$refs.hooperContainer.slideNext();
    },
    updateCarousel(payload) {
      this.myCarouselData = payload.currentSlide;
    },
    afterSlide({ currentSlide }) {
      this.setDotFromSlideIndex(currentSlide);
    },
    setDotFromSlideIndex(slideIndex) {
      this.dotActive = Math.ceil((slideIndex + 1) / this.breakpoints);
    },
    slideTo(dotIndex) {
      const slideIndex = (dotIndex - 1) * this.breakpoints + 1;

      this.setDotFromSlideIndex(slideIndex);

      this.$refs.hooperContainer.slideTo(slideIndex);
      this.$refs.hooperContainer.restartTimer();
    },
  },
  watch: {
    carouselData() {
      this.$refs.hooperContainer.slideTo(this.carouselData);
    },
    breakpoints() {
      this.$refs.hooperContainer.slideTo(0);
      this.$refs.hooperContainer.restartTimer();
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel-slider::v-deep {
  ul,
  ol {
    padding-left: 0 !important;
  }
}

.slides {
  @media screen and (max-width: 1264px) {
    padding-left: 40px;
    padding-right: 40px;
  }
}

.carousel-slider {
  &-nav {
    height: 0;
    left: -100px;
    margin-top: -20px;
    position: absolute;
    right: -100px;
    top: 50%;

    @media screen and (max-width: 1264px) {
      left: 0;
      right: 0;
    }

    &-item {
      border-radius: 50%;
      height: 40px;
      width: 40px;
    }
  }
}

.carousel-pagination {
  gap: 6px;

  .carousel-pagitation-dot {
    background-color: #8d99a6;
    border-radius: 50%;
    cursor: pointer;
    height: 12px;
    transition: 0.3s;
    width: 12px;

    &.active {
      background-color: #3e4c59;
    }
  }
}
</style>
